"use client";

import { ThemePill } from "@/src/components/theme-pill";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import type { StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";

function DetailSection({ title, body }: { title: string; body: string }) {
  return (
    <div className="mt-3">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

export function StediEligibilitySummary({ check }: { check: StediEligibilityCheck }) {
  const display = resolveStediEligibilityDisplay(check);
  const checkedAt = new Date(check.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  const checkFailureReason =
    [display.primaryErrorCode, display.primaryErrorDescription].filter(Boolean).join(": ") || null;

  if (display.checkFailed) {
    return (
      <div className="mt-4 mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h3 className="font-semibold text-foreground">Insurance Eligibility</h3>
          <ThemePill theme="destructive">Check Failed</ThemePill>
          <span className="text-xs font-normal text-muted-foreground">Checked {checkedAt}</span>
        </div>
        {checkFailureReason && <DetailSection title="Failure Reason" body={checkFailureReason} />}
      </div>
    );
  }

  return (
    <div className="mt-4 mb-6">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-2">
        <div>
          <span className="font-medium text-foreground">Payer: </span>
          <span className="font-normal text-muted-foreground">{display.payerName || "N/A"}</span>
        </div>
        <div>
          <span className="font-medium text-foreground">Plan type: </span>
          <span className="font-normal text-muted-foreground">{display.planType || "N/A"}</span>
        </div>
        <div>
          <span className="font-medium text-foreground">Plan name: </span>
          <span className="font-normal text-muted-foreground">{display.planName || "N/A"}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-semibold text-foreground">Insurance Eligibility</h3>
        {display.eligibilityStatus === "active" && <ThemePill theme="success">Active</ThemePill>}
        {display.eligibilityStatus === "inactive" && <ThemePill theme="destructive">Inactive</ThemePill>}
        {display.eligibilityStatus === "unknown" && <ThemePill theme="muted">Unknown</ThemePill>}
        {display.officeVisitRequiresAuth === true && (
          <ThemePill theme="warn" className="whitespace-nowrap">
            Requires Authorization
          </ThemePill>
        )}
        {display.officeVisitRequiresAuth == null && (
          <ThemePill theme="muted" className="whitespace-nowrap">
            Authorization Unknown
          </ThemePill>
        )}
        {display.officeVisitRequiresAuth === false && (
          <ThemePill theme="success" className="whitespace-nowrap">
            Does Not Require Authorization
          </ThemePill>
        )}
        <span className="text-xs font-normal text-muted-foreground">Checked {checkedAt}</span>
      </div>
      {display.officeVisitAuthReason && (
        <DetailSection title="Authorization Decision" body={display.officeVisitAuthReason} />
      )}
    </div>
  );
}
